/**
 * ===================================================================================
 * 📁 경로: services/frontend/src/pages/AnalysisPage.js
 * 📌 프로그램명: PDF 분석 요청 및 실시간 로그 페이지
 * 🧩 개요:
 * - 사용자가 건강검진 PDF 파일을 업로드하고, AI 분석 파이프라인의 진행 상황을
 * 실시간 로그 형태로 확인할 수 있는 UI를 제공합니다.
 * - 복잡한 상태 관리 및 비동기 스트리밍 로직은 `useAnalysisStream` 커스텀 훅으로
 * 분리하여, 이 컴포넌트는 UI 렌더링에만 집중하도록 설계되었습니다.
 * ===================================================================================
 */
import React, { useCallback } from 'react';
import DuplicateFoundModal from '../components/DuplicateFoundModal';
import { useAnalysisStream } from '../hooks/useAnalysisStream'; // ✨ 커스텀 훅 임포트

/**
 * [Why]
 * AI 분석 요청의 상세 진행 과정을 사용자에게 투명하게 보여주기 위한 페이지 컴포넌트입니다.
 * `MainPage`가 최종 결과물에 집중하는 반면, 이 페이지는 각 백엔드 에이전트의 호출과
 * 완료 상태를 실시간으로 추적하는 데 중점을 둡니다.
 *
 * [How]
 * - **로직 분리**: 핵심적인 상태 관리(isLoading, logs 등)와 비동기 통신 로직(startAnalysis,
 * cancelAnalysis)은 모두 `useAnalysisStream` 커스텀 훅에 위임합니다.
 * - **상태 기반 렌더링**: `useAnalysisStream` 훅으로부터 받은 상태 값에 따라 조건부로
 * UI를 렌더링합니다. 예를 들어, `isLoading`이 true이면 로딩 UI와 중단 버튼을,
 * `isDuplicate`가 true이면 중복 처리 모달을 화면에 표시합니다.
 * - **이벤트 핸들링**: 사용자가 파일을 선택(`onChange`)하거나 버튼을 클릭(`onClick`)하면,
 * 훅에서 제공하는 해당 핸들러 함수(`startAnalysis`, `cancelAnalysis` 등)를 호출하여
 * 상태 변경을 트리거합니다.
 *
 * @component
 * @returns {JSX.Element} AnalysisPage 컴포넌트의 JSX 엘리먼트.
 */
function AnalysisPage() {
    // ✨ [개선 제안 3] 커스텀 훅을 사용하여 상태와 함수를 한번에 가져오기
    const {
        isLoading,
        logs,
        isDuplicate,
        duplicateReportId,
        notLinkedMsg,
        startAnalysis,
        cancelAnalysis,
        closeDuplicateModal, // ✨ 훅으로부터 모달 닫기 함수를 가져옵니다.
    } = useAnalysisStream();

    /**
     * [Why]
     * 사용자가 파일 선택 input을 통해 PDF 파일을 선택했을 때, 분석 프로세스를
     * 시작하기 위한 이벤트 핸들러입니다.
     *
     * [How]
     * `useCallback` 훅을 사용하여 `startAnalysis` 함수가 변경될 때만 이 핸들러 함수가
     * 재생성되도록 메모이제이션하여 최적화를 수행합니다.
     * 이벤트 객체에서 파일(file)을 추출하고, `startAnalysis` 훅 함수를 호출하여
     * 파일과 함께 분석 시작을 요청합니다.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} e - 파일 input 요소의 change 이벤트 객체.
     */
    const onFileChange = useCallback(async (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            startAnalysis(file);
        }
    }, [startAnalysis]);


    // UI 렌더링 부분은 기존 구조와 거의 동일하게 유지
    return (
        <div style={{ padding: 16 }}>
            <h2>건강검진 PDF 분석</h2>
            <input type="file" accept="application/pdf" onChange={onFileChange} disabled={isLoading} />
            
            {isLoading && (
                <div style={{ marginTop: 12 }}>
                    <div>분석 중... (네트워크 상태에 따라 최대 1분 소요될 수 있습니다)</div>
                    <button onClick={cancelAnalysis} style={{ marginTop: 8 }}>중단</button>
                </div>
            )}

            <div style={{ marginTop: 12, border: '1px solid #eee', padding: '8px 16px', minHeight: '200px' }}>
                <strong>[분석 로그]</strong>
                <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                    {logs.map((log, idx) => (
                        <li key={idx} style={{ marginTop: 4 }}>{log}</li>
                    ))}
                </ul>
            </div>

            <DuplicateFoundModal
                isOpen={isDuplicate}
                // ✨✨✨ [핵심 수정] 훅에서 제공하는 함수를 onClose에 연결합니다. ✨✨✨
                onClose={closeDuplicateModal}
                reportId={duplicateReportId}
            />

            {notLinkedMsg && (
                 <div style={{ marginTop: 12, padding: 12, border: '1px solid #ddd', borderRadius: 8, background: '#fffbe6' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 6 }}>안내</div>
                    <div>{notLinkedMsg}</div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                        동일 사용자의 기존 리포트가 생성되지 않았거나 연결되지 않은 상태일 수 있습니다. 필요 시 다시 업로드하거나, 관리자에게 문의해 주세요.
                    </div>
                </div>
            )}
        </div>
    );
}

export default AnalysisPage;